import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabaseClient'
import {
  Search, ExternalLink, Mail, Globe, Briefcase,
  Link2, BookOpen, CheckCircle2, XCircle, Filter,
} from 'lucide-react'

const RUOLI_SUGGERITI = [
  'Tutti', 'Motion Designer', 'Editor', 'Colorist', 'Director', 'DOP',
  'Sound Designer', 'Fotografo', 'Illustratore', 'Grafico', 'Copywriter', 'Social Manager',
]

const Avatar = ({ profile, size = 'md' }) => {
  const dim = size === 'lg' ? 'w-16 h-16 text-xl' : 'w-12 h-12 text-base'
  const displayName = profile.nickname || profile.full_name || '?'
  const letter = displayName[0]?.toUpperCase() ?? '?'
  const avatarUrl = profile.avatar_url ? `${profile.avatar_url}?t=0` : null
  return (
    <div className={`${dim} rounded-full bg-violet-600 flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0`}>
      {avatarUrl
        ? <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" />
        : <span>{letter}</span>}
    </div>
  )
}

const ensureHttp = (url) => {
  if (!url) return url
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

const LinkBtn = ({ href, icon: Icon, label }) => {
  if (!href) return null
  return (
    <a href={ensureHttp(href)} target="_blank" rel="noopener noreferrer"
      title={label}
      className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-violet-400 transition-colors truncate">
      <Icon size={13} className="flex-shrink-0" />
      <span className="truncate">{label}</span>
      <ExternalLink size={11} className="flex-shrink-0 opacity-60" />
    </a>
  )
}

const CreativoCard = ({ creativo }) => {
  const nome = creativo.nickname || creativo.full_name || 'Utente'
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 flex flex-col gap-4 hover:border-gray-700 transition-colors">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Avatar profile={creativo} size="lg" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold text-base truncate">{nome}</h3>
            {creativo.disponibile
              ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400 bg-emerald-400/10 px-2 py-0.5 rounded-full flex-shrink-0">
                  <CheckCircle2 size={11} /> Disponibile
                </span>
              : <span className="inline-flex items-center gap-1 text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full flex-shrink-0">
                  <XCircle size={11} /> Non disponibile
                </span>
            }
          </div>
          {creativo.ruolo_creativo && (
            <p className="text-violet-400 text-sm mt-0.5 font-medium">{creativo.ruolo_creativo}</p>
          )}
        </div>
      </div>

      {/* Bio */}
      {creativo.bio && (
        <p className="text-gray-400 text-sm leading-relaxed line-clamp-3">{creativo.bio}</p>
      )}

      {/* Links */}
      <div className="flex flex-col gap-1.5">
        {creativo.email_contatto && (
          <a href={`mailto:${creativo.email_contatto}`}
            className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-violet-400 transition-colors truncate">
            <Mail size={13} className="flex-shrink-0" />
            <span className="truncate">{creativo.email_contatto}</span>
          </a>
        )}
        <LinkBtn href={creativo.linkedin_url} icon={Link2} label="LinkedIn" />
        <LinkBtn href={creativo.portfolio_url} icon={BookOpen} label="Portfolio" />
        <LinkBtn href={creativo.website_url} icon={Globe} label="Sito web" />
      </div>
    </div>
  )
}

const Creativi = () => {
  const [creativi, setCreativi] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [ruoloFilter, setRuoloFilter] = useState('Tutti')
  const [soloDisponibili, setSoloDisponibili] = useState(false)

  const fetchCreativi = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase
      .from('profiles')
      .select('id, full_name, nickname, avatar_url, ruolo_creativo, bio, linkedin_url, website_url, portfolio_url, email_contatto, disponibile')
      .or('ruolo_creativo.not.is.null,bio.not.is.null,linkedin_url.not.is.null,portfolio_url.not.is.null')
      .order('full_name', { ascending: true })
    setCreativi(data || [])
    setLoading(false)
  }, [])

  useEffect(() => { fetchCreativi() }, [fetchCreativi])

  const filtered = creativi.filter(c => {
    const nome = (c.nickname || c.full_name || '').toLowerCase()
    const ruolo = (c.ruolo_creativo || '').toLowerCase()
    const bio = (c.bio || '').toLowerCase()
    const q = search.toLowerCase()
    const matchSearch = !q || nome.includes(q) || ruolo.includes(q) || bio.includes(q)
    const matchRuolo = ruoloFilter === 'Tutti' || ruolo.includes(ruoloFilter.toLowerCase())
    const matchDisp = !soloDisponibili || c.disponibile
    return matchSearch && matchRuolo && matchDisp
  })

  // unique ruoli from data for dynamic pills
  const ruoliPresenti = ['Tutti', ...Array.from(new Set(creativi.map(c => c.ruolo_creativo).filter(Boolean)))]
  const pillRuoli = ruoliPresenti.length > 2 ? ruoliPresenti : RUOLI_SUGGERITI

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Database Creativi</h1>
          <p className="text-gray-500 text-sm mt-1">
            {creativi.length} {creativi.length === 1 ? 'creativo registrato' : 'creativi registrati'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setSoloDisponibili(v => !v)}
            className={`flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${
              soloDisponibili
                ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400'
                : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-600'
            }`}>
            <Filter size={14} />
            Solo disponibili
          </button>
        </div>
      </div>

      {/* Ricerca */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Cerca per nome, ruolo, bio..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-gray-900 border border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-violet-500 transition-colors"
        />
      </div>

      {/* Ruolo filter pills */}
      <div className="flex gap-2 flex-wrap mb-6">
        {pillRuoli.map(r => (
          <button key={r} onClick={() => setRuoloFilter(r)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              ruoloFilter === r
                ? 'bg-violet-600 border-violet-600 text-white'
                : 'bg-gray-900 border-gray-800 text-gray-400 hover:border-gray-600 hover:text-white'
            }`}>
            {r !== 'Tutti' && <Briefcase size={12} />}
            {r}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <p className="text-gray-500 text-sm">
            {creativi.length === 0
              ? 'Nessun creativo ha ancora compilato il proprio profilo creativo.'
              : 'Nessun risultato per questa ricerca.'}
          </p>
          {creativi.length === 0 && (
            <p className="text-gray-600 text-xs mt-2">
              Vai in <span className="text-violet-400">Impostazioni → Profilo creativo</span> per aggiungere i tuoi dati.
            </p>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(c => <CreativoCard key={c.id} creativo={c} />)}
        </div>
      )}
    </div>
  )
}

export default Creativi
